// src/api/index.js
import axios from 'axios'
import { showToast } from 'vant'
import { useAuthManager } from '@/state/user'
import router from '@/navigation'
import { decryptResponseSync } from '@/helpers/crypto'
import { hasPrivacyConsented } from '@/helpers/privacyConsent'

// 1. 创建axios实例
const service = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL, // 从环境变量读取
  //baseURL: "/api/", // 从环境变量读取
  timeout: 15000, // 超时时间
  headers: {
    'Content-Type': 'application/json;charset=UTF-8'
  }
})

// 2. 请求拦截器（处理鉴权）
service.interceptors.request.use(
  (config) => {
    // 审核要求：用户同意隐私政策前不发起任何网络请求（埋点等预加载请求一并拦截）
    if (!hasPrivacyConsented()) {
      console.warn('⛔ 隐私政策未同意，已拦截请求:', config.url)
      return Promise.reject(new Error('PRIVACY_NOT_CONSENTED'))
    }

    const authManager = useAuthManager()

    // 从 localStorage 获取最新 token（避免 store 状态不同步）
    const token = localStorage.getItem('token')

    // 添加token（如果存在）
    if (token) {
      config.headers['token'] = token
    } else {
      // 没有 token，删除请求头中的 token
      delete config.headers['token']
    }

    // 微信H5特殊处理
    if (import.meta.env.VITE_APP_ENV === 'wechat') {
      config.headers['Client-Type'] = 'wechat-h5'
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 3. 响应拦截器（统一错误处理）
service.interceptors.response.use(
  (response) => {
    const res = response.data
    // 业务状态码判断
    if (res.success === false) {
      // 获取错误信息：支持两种格式
      // 1. {success: false, message: "..."}
      // 2. {success: false, data: {errMsg: "..."}}
      let errorMsg = '请求失败'
      if (res.message) {
        errorMsg = res.message
      } else if (res.data && res.data.errMsg) {
        errorMsg = res.data.errMsg
      }

      // 抛出异常，让调用方的 catch 能捕获
      const error = new Error(errorMsg)
      error.response = response
      error.data = res.data
      error.errCode = res.data?.errCode
      error.errMsg = res.data?.errMsg
      return Promise.reject(error)
    }
    // 尝试解密响应数据（如果启用了接口加密）
    const decryptedRes = decryptResponseSync(res)

    // 直接返回完整响应，由各API函数处理 .data
    return decryptedRes
  },
  (error) => {
    // 取消的请求不处理（主动取消，不是错误）
    if (axios.isCancel(error)) {
      return Promise.reject(error)
    }

    // 隐私政策未同意被请求拦截器拦掉的请求：静默失败，
    // 不进入下方错误包装（否则会弹"网络连接异常"）
    if (error && error.message === 'PRIVACY_NOT_CONSENTED') {
      return Promise.reject(error)
    }

    // HTTP状态码处理
    if (error.response) {
      switch (error.response.status) {
        case 400:
          error.message = '请求参数错误'
          break
        case 401:
          error.message = '请先登录'
          // 可以选择跳转到登录页
          // router.push('/login')
          break
        case 500:
          error.message = '服务器内部错误'
          break
        default:
          error.message = `网络错误 (${error.response.status})`
      }
    } else if (error.message && error.message.includes('timeout')) {
      error.message = '请求超时'
    } else {
      error.message = '网络连接异常'
    }

    if (error.message) {
      showToast(error.message)
    }

    return Promise.reject(error)
  }
)

// 4. 封装常用方法
export const http = {
  get(url, params) {
    return service.get(url, { params })
  },

  post(url, data) {
    return service.post(url, data)
  },

  put(url, data) {
    return service.put(url, data)
  },

  delete(url) {
    return service.delete(url)
  },

  // 微信JS-SDK上传专用
  wxUpload(url, file) {
    const formData = new FormData()
    formData.append('file', file)
    return service.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }
}

// 5. 导出默认实例
export default service